// PostHog est chargé à la demande (import dynamique) : ~120 Ko sortent du
// bundle initial pour accélérer le premier affichage. Les événements émis
// avant la fin du chargement sont mis en file puis rejoués (on ne perd rien).
import { getStoredConsent } from "./utils/cookieConsent";

type Consent = { analytics?: boolean } | null;

let ph: any = null; // instance posthog une fois chargée
let inited = false;
let loadPromise: Promise<any> | null = null;
let pending: Array<{ event: string; props?: Record<string, unknown> }> = [];
let currentConsent: Consent = null;
let disabled = false; // clé absente → PostHog inactif (on ne met rien en file)

function loadPosthog(): Promise<any> {
  if (!loadPromise) {
    loadPromise = import("posthog-js")
      .then((m: any) => (m && m.default ? m.default : m))
      .catch((e) => {
        if (process.env.NODE_ENV !== "production") console.warn("[analytics] PostHog load échec", e);
        return null;
      });
  }
  return loadPromise;
}

export function initAnalytics() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
  if (!key || key === "phc_TA_COMPLETER") {
    disabled = true;
    if (process.env.NODE_ENV !== "production") console.info("[analytics] PostHog : clé manquante — désactivé");
    return;
  }
  if (inited) {
    applyConsent(getStoredConsent());
    return;
  }

  loadPosthog().then((posthog) => {
    if (!posthog || inited) return;
    try {
      posthog.init(key, {
        api_host: host,
        person_profiles: "identified_only",
        autocapture: true,
        capture_pageview: false,
      });
      ph = posthog;
      inited = true;
      applyConsent(currentConsent ?? getStoredConsent());
      // Rejoue les événements émis pendant le chargement (ex. 1re page vue).
      const q = pending;
      pending = [];
      for (const it of q) {
        try {
          ph.capture(it.event, it.props || {});
        } catch (e) {
          /* ignore */
        }
      }
      if (process.env.NODE_ENV !== "production")
        console.info("[analytics] PostHog initialisé (host " + host + ")");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("[analytics] PostHog init échec", e);
    }
  });
}

export function applyConsent(consent: Consent) {
  currentConsent = consent;
  if (!inited || !ph) return;
  try {
    if (consent?.analytics) ph.opt_in_capturing();
    else ph.opt_out_capturing();
    if (process.env.NODE_ENV !== "production")
      console.info("[analytics] consentement =", consent ? `${consent.analytics ? "accepté" : "refusé"}` : "non défini");
  } catch (e) {
    /* ignore */
  }
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (disabled) return;
  if (inited && ph) {
    try {
      ph.capture(eventName, properties || {});
    } catch (e) {
      /* ignore */
    }
    return;
  }
  // Chargement en cours → on met en file (bornée) le temps que PostHog soit prêt.
  if (loadPromise && pending.length < 100) pending.push({ event: eventName, props: properties });
}

// Vue de page SPA → événement $pageview PostHog standard.
export function trackPageView(url?: string) {
  trackEvent("$pageview", {
    current_url: url || window.location.href,
    $pathname: window.location.pathname,
    $search: window.location.search,
  });
}

// À brancher sur l'événement "anyhomes:consent-updated" (détail = { analytics, marketing })
export function onConsentUpdated(detail?: { analytics?: boolean; marketing?: boolean }) {
  applyConsent(detail ?? null);
}
